import type { FC } from "react";
import Tooltip from "@mui/material/Tooltip";
import { Box, Stack } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import { ExternalLinkIcon } from "../../shared/icons";
import { AssistantMarkdown } from "./AssistantMarkdown";

const URL_PATTERN = /https?:\/\/[^\s<>"')\]]+/g;
const MARKDOWN_LINK_PATTERN = /\[([^\]\n]+)\]\(\s*https?:\/\/[^\s)]+\s*\)/g;
const LEAD_IN_PATTERN = /^\s*[\p{L}]+(\s[\p{L}]+)?\s*$/u;

type AssistantMessageTextProps = { text: string; isStreaming?: boolean };

export const AssistantMessageText: FC<AssistantMessageTextProps> = ({ text, isStreaming }) => {
  const sources = sourcesOf(text);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <AssistantMarkdown text={sources.length > 0 ? withoutLinks(text) : text} isStreaming={isStreaming} />
      {sources.length > 0 && <Sources urls={sources} />}
    </Box>
  );
};

const Sources: FC<{ urls: Array<string> }> = ({ urls }) => {
  const { t } = useTranslation();

  return (
    <Stack
      direction="row"
      role="group"
      aria-label={t("Assistant.Message.Sources")}
      sx={{ mt: 0.5, gap: 0.75, alignItems: "center", flexWrap: "wrap" }}
    >
      {urls.map(url => (
        <Tooltip key={url} title={url} enterDelay={400}>
          <Box
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              "display": "inline-flex",
              "alignItems": "center",
              "gap": 0.5,
              "px": 1,
              "py": 0.25,
              "borderRadius": 999,
              "bgcolor": theme => theme.palette.grey[100],
              "color": theme => theme.palette.text.secondary,
              "fontSize": theme => theme.typography.pxToRem(12),
              "lineHeight": 1.5,
              "textDecoration": "none",
              "transition": "background-color 120ms",
              "&:hover": { bgcolor: theme => theme.palette.grey[200], color: theme => theme.palette.text.primary },
              "&:focus-visible": { outline: theme => `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
            }}
          >
            {siteOf(url)}
            <ExternalLinkIcon size={11} />
          </Box>
        </Tooltip>
      ))}
    </Stack>
  );
};

const cleanUrl = (match: string) => match.replace(/[.,;:!?]+$/, "");

const sourcesOf = (text: string): Array<string> => [
  ...new Set(Array.from(text.matchAll(URL_PATTERN), match => cleanUrl(match[0]))),
];

const withoutLinks = (text: string): string =>
  text
    .split("\n")
    .map(line => {
      if (!line.match(URL_PATTERN)) return line;
      const stripped = line
        .replace(MARKDOWN_LINK_PATTERN, "$1")
        .replace(URL_PATTERN, match => match.slice(cleanUrl(match).length))
        .replace(/\(\s*\)/g, "")
        .replace(/\s+([.,;:!?])/g, "$1")
        .replace(/[ \t]{2,}/g, " ")
        .trimEnd()
        .replace(/:$/, "");
      return /[\p{L}\p{N}]/u.test(stripped) && !LEAD_IN_PATTERN.test(stripped) ? stripped : null;
    })
    .filter((line): line is string => line !== null)
    .join("\n");

const siteOf = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};
